import { PaperPlaneIcon } from '@radix-ui/react-icons';
import { type UseComboboxState, type UseComboboxStateChangeOptions, useCombobox } from 'downshift';
import { useState } from 'react';

import { cn } from '~/styles/extended-theme.ts';

type ComboboxSelectItem = string;

interface Props {
  name: string;
  items: ComboboxSelectItem[];
  value: string;
  onChange: (value: string) => void;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  className?: string;
  onCurrentLocationClick?: React.MouseEventHandler<HTMLButtonElement>;
  onCityClick?: (e: React.MouseEvent<HTMLButtonElement>, city: string) => void;
}

const stateReducer = (
  _state: UseComboboxState<ComboboxSelectItem>,
  actionAndChanges: UseComboboxStateChangeOptions<ComboboxSelectItem>,
) => {
  const { type, changes } = actionAndChanges;

  switch (type) {
    case useCombobox.stateChangeTypes.InputClick:
      return { ...changes, isOpen: changes.isOpen };

    default:
      return changes;
  }
};

export function CityCombobox({
  value,
  name,
  items: allItems,
  inputProps,
  className,
  onChange,
  onCurrentLocationClick,
  onCityClick,
}: Props) {
  const getFilteredItems = (inputValue?: string) => {
    return allItems.filter(
      (item) => inputValue === undefined || item.toLowerCase().startsWith(inputValue.toLowerCase()),
    );
  };

  const [items, setItems] = useState(getFilteredItems(value));
  const { className: inputClassName, ...otherInputProps } = inputProps ?? {};

  const cb = useCombobox<ComboboxSelectItem>({
    items,
    stateReducer,
    inputValue: value,
    onInputValueChange: ({ inputValue }) => {
      onChange(inputValue ?? '');
      setItems(getFilteredItems(inputValue));
    },
  });

  const handleCurrentLocationClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onCurrentLocationClick && onCurrentLocationClick(e);
  };

  return (
    <div className={className}>
      <input
        {...cb.getInputProps({
          ...otherInputProps,
          name,
          type: 'text',
          className: cn('bg-transparent font-light', inputClassName),
          placeholder: 'City or zip code',
          onKeyDown: (e) => {
            if (e.key === 'Enter' && cb.highlightedIndex === -1) {
              // submits the form if the user presses enter and there's no highlighted item
              // see https://github.com/downshift-js/downshift#event-handlers and https://github.com/downshift-js/downshift/issues/734
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e.nativeEvent as any).preventDownshiftDefault = true;
            }
          },
        })}
      />

      <ul
        className={`absolute z-10 mt-2.5 max-h-80 w-72 overflow-y-auto rounded-md border border-solid bg-white p-2 shadow-md ${
          !cb.isOpen && 'hidden'
        }`}
        {...cb.getMenuProps()}
      >
        {cb.isOpen && cb.inputValue.length >= 3
          ? items.map((item, index) => (
              <li
                key={item}
                className={cn('cursor-pointer px-2 py-1 text-primary', {
                  'font-bold': cb.highlightedIndex === index,
                })}
                {...cb.getItemProps({ item, index })}
              >
                <button onClick={(e) => onCityClick?.(e, item)}>{item}</button>
              </li>
            ))
          : null}
        <button
          type="button"
          className="my-2 w-full rounded-lg p-2 text-body2 text-secondary hover:bg-gray-200"
          onClick={handleCurrentLocationClick}
        >
          <div className="flex gap-[10px]">
            <PaperPlaneIcon className="rotate-[-135deg]" />
            Current Location
          </div>
        </button>
      </ul>
    </div>
  );
}
